import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, FileSearch, LayoutDashboard, CheckCircle, Clock } from 'lucide-react';
import OnboardingCard, { OnboardingButton } from '../components/OnboardingCard';
import { isImportTerminalFailure } from '../../../utils/creditImportStatus';

const PROGRESS_STAGES = [
  { id: 'connecting', label: 'Securing connection', icon: Lock },
  { id: 'pulling', label: 'Pulling your report', icon: FileSearch },
  { id: 'organizing', label: 'Organizing accounts', icon: LayoutDashboard },
  { id: 'finding', label: 'Finding negative items', icon: Shield },
  { id: 'preparing', label: 'Preparing your dashboard', icon: CheckCircle },
];

const RUNNING_PROGRESS_CAP = 90;
const SLOW_IMPORT_MS = 90000;
const MIN_RUNNING_PROGRESS = 20;

const isTerminalFailure = (importStatus) => isImportTerminalFailure(importStatus);

const hasReportReady = (importStatus) =>
  Boolean(importStatus?.report_id) &&
  (importStatus?.status === 'completed' || importStatus?.status === 'success');

const computeDisplayProgress = ({ importStatus, elapsedMs, smoothedFloor }) => {
  if (hasReportReady(importStatus)) {
    return 100;
  }

  if (isTerminalFailure(importStatus)) {
    return smoothedFloor;
  }

  const timeProgress =
    MIN_RUNNING_PROGRESS +
    Math.min((elapsedMs / SLOW_IMPORT_MS) * (RUNNING_PROGRESS_CAP - MIN_RUNNING_PROGRESS), RUNNING_PROGRESS_CAP - MIN_RUNNING_PROGRESS);

  let apiProgress = null;
  if (importStatus?.progress != null && Number.isFinite(Number(importStatus.progress))) {
    const raw = Number(importStatus.progress);
    apiProgress = MIN_RUNNING_PROGRESS + (raw / 100) * (RUNNING_PROGRESS_CAP - MIN_RUNNING_PROGRESS);
  }

  const blended = Math.max(timeProgress, apiProgress ?? 0, smoothedFloor);
  return Math.min(Math.max(blended, MIN_RUNNING_PROGRESS), RUNNING_PROGRESS_CAP);
};

const ImportLoadingStep = ({
  importStatus,
  importStartedAt,
  onContinueToDashboard,
  onKeepWaiting,
  showSessionRestart = false,
  onRestartImport,
}) => {
  const [elapsedMs, setElapsedMs] = useState(0);
  const [smoothedProgress, setSmoothedProgress] = useState(MIN_RUNNING_PROGRESS);
  const [slowImportOffered, setSlowImportOffered] = useState(false);
  const [dismissedSlowOffer, setDismissedSlowOffer] = useState(false);

  const failed = isTerminalFailure(importStatus);
  const stillRunning = !failed && !hasReportReady(importStatus);
  const showSlowImportBanner = stillRunning && slowImportOffered && !dismissedSlowOffer;

  useEffect(() => {
    if (!importStartedAt) return undefined;

    const start = new Date(importStartedAt).getTime();
    const tick = () => setElapsedMs(Date.now() - start);
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [importStartedAt]);

  useEffect(() => {
    if (elapsedMs >= SLOW_IMPORT_MS && stillRunning) {
      setSlowImportOffered(true);
    }
  }, [elapsedMs, stillRunning]);

  const targetProgress = useMemo(
    () => computeDisplayProgress({ importStatus, elapsedMs, smoothedFloor: smoothedProgress }),
    [importStatus, elapsedMs, smoothedProgress]
  );

  useEffect(() => {
    if (hasReportReady(importStatus)) {
      setSmoothedProgress(100);
      return undefined;
    }

    const interval = setInterval(() => {
      setSmoothedProgress((prev) => {
        if (prev >= targetProgress) return prev;
        const step = Math.max(0.35, (targetProgress - prev) * 0.12);
        return Math.min(prev + step, targetProgress);
      });
    }, 120);

    return () => clearInterval(interval);
  }, [targetProgress, importStatus]);

  const currentStage = useMemo(() => {
    const p = smoothedProgress;
    if (p < 32) return 0;
    if (p < 48) return 1;
    if (p < 64) return 2;
    if (p < 80) return 3;
    return 4;
  }, [smoothedProgress]);

  const currentStageData = PROGRESS_STAGES[currentStage];
  const CurrentIcon = currentStageData?.icon || Shield;

  const statusMessage = failed
    ? importStatus?.error || importStatus?.message || 'Import could not be completed.'
    : hasReportReady(importStatus)
      ? 'Finishing up...'
      : showSlowImportBanner
        ? 'This is taking longer than usual. You can continue to dashboard while we finish in the background.'
        : importStatus?.message || currentStageData?.label || 'Still importing...';

  return (
    <OnboardingCard showGlow={true} glowIntensity="strong">
      <motion.div
        className="p-8 sm:p-12 py-16 sm:py-20"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex justify-center mb-8">
          <div className="relative">
            <motion.div
              className="absolute inset-0 rounded-full bg-emerald-500/20"
              animate={{ scale: [1, 1.4, 1], opacity: [0.5, 0, 0.5] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              style={{ width: 96, height: 96 }}
            />
            <motion.div
              className="relative w-24 h-24 rounded-full border-4 border-slate-700"
              style={{ borderTopColor: '#10b981', borderRightColor: '#10b981' }}
              animate={{ rotate: 360 }}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
            />
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              key={currentStage}
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <CurrentIcon size={32} className="text-emerald-400" />
            </motion.div>
          </div>
        </div>

        <motion.div
          key={statusMessage}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-white">
            {failed ? 'Import needs attention' : "We're building your dashboard now"}
          </h2>
          <p className={`text-lg mt-3 ${failed ? 'text-amber-300' : 'text-slate-400'}`}>
            {statusMessage}
          </p>
        </motion.div>

        {!failed && (
          <div className="mt-10 max-w-md mx-auto">
            <div className="relative h-2 bg-slate-700 rounded-full overflow-hidden">
              <motion.div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${smoothedProgress}%` }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              />
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
              />
            </div>
            <div className="flex justify-between mt-2 text-sm text-slate-500">
              <span>{Math.round(smoothedProgress)}%</span>
              <span>{stillRunning ? 'Still importing...' : 'Processing'}</span>
            </div>
          </div>
        )}

        {showSessionRestart && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 max-w-lg mx-auto rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4"
            data-testid="onboarding-import-restart"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-sm text-emerald-100/90">
                We need to restart your secure import session.
              </p>
              <OnboardingButton onClick={onRestartImport} className="sm:flex-shrink-0">
                Restart Import
              </OnboardingButton>
            </div>
          </motion.div>
        )}

        {showSlowImportBanner && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 max-w-lg mx-auto rounded-xl border border-amber-500/30 bg-amber-500/10 p-4"
          >
            <div className="flex items-start gap-3">
              <Clock size={18} className="text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 space-y-3">
                <p className="text-sm text-amber-100/90">
                  This is taking longer than usual. You can continue to dashboard while we finish in the background.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <OnboardingButton
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onContinueToDashboard?.();
                    }}
                    className="flex-1"
                  >
                    Continue to Dashboard
                  </OnboardingButton>
                  <OnboardingButton
                    variant="ghost"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      setDismissedSlowOffer(true);
                      onKeepWaiting?.();
                    }}
                    className="flex-1"
                  >
                    Keep Waiting
                  </OnboardingButton>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        <div className="mt-8 flex justify-center gap-2">
          {PROGRESS_STAGES.map((stage, index) => (
            <motion.div
              key={stage.id}
              className={`w-2 h-2 rounded-full ${index <= currentStage ? 'bg-emerald-500' : 'bg-slate-700'}`}
              animate={index === currentStage ? { scale: [1, 1.3, 1] } : {}}
              transition={{ duration: 1, repeat: index === currentStage ? Infinity : 0 }}
            />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-10 flex flex-wrap justify-center gap-4"
        >
          {[
            { icon: Shield, text: 'Encrypted session' },
            { icon: CheckCircle, text: 'No score impact' },
          ].map((badge, index) => (
            <div
              key={index}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-700/50 border border-slate-600"
            >
              <badge.icon size={14} className="text-emerald-400" />
              <span className="text-xs text-slate-300">{badge.text}</span>
            </div>
          ))}
        </motion.div>

        {!showSlowImportBanner && stillRunning && (
          <p className="text-center text-sm text-slate-500 mt-6">
            Usually completes in under 90 seconds
          </p>
        )}
      </motion.div>
    </OnboardingCard>
  );
};

export default ImportLoadingStep;
